import { useMemo, useCallback } from 'react'
import { toast } from 'sonner'
import type { Deal, Proposta, PropostaItem, ComboVariation } from '@/types'
import type { LayoutConfig } from '@/utils/dealCalculations'
import { patchProposalStudioPresentation } from './proposalStudioSave'
import { getFriendlyError } from '@/utils/getFriendlyError'

export function useProposalTabStudio(params: {
  proposals: Proposta[]
  studioPlan: PropostaItem | null
  setStudioPlan: (v: PropostaItem | null) => void
  setProposals: React.Dispatch<React.SetStateAction<Proposta[]>>
  onDealPatched: (updates: Partial<Deal>) => void
}) {
  const { proposals, studioPlan, setStudioPlan, setProposals, onDealPatched } = params

  const targetProposalForStudio = useMemo(() => {
    if (!studioPlan) return null
    const byItemId = proposals.find((p) => p.itens?.some((i) => i.id === studioPlan.id))
    if (byItemId) return byItemId
    const propostaId = (studioPlan as PropostaItem & { propostaId?: string }).propostaId
    if (propostaId) {
      const byPropostaId = proposals.find((p) => p.id === propostaId)
      if (byPropostaId) return byPropostaId
    }
    return proposals[0] || null
  }, [studioPlan, proposals])

  const saveStudioPresentation = useCallback(
    async (
      savedItems: PropostaItem[],
      savedLayout: LayoutConfig | undefined,
      savedComboVariations: ComboVariation[] | undefined,
    ) => {
      if (!targetProposalForStudio) return
      const toastId = toast.loading('Salvando apresentação...')
      try {
        const saved = await patchProposalStudioPresentation(
          targetProposalForStudio,
          savedItems,
          savedLayout,
          savedComboVariations,
        )
        setProposals((prev) => prev.map((p) => (p.id === targetProposalForStudio.id ? saved : p)))
        const valor = Number(saved.totalFinal ?? 0)
        if (valor > 0) {
          onDealPatched({ valor, desconto: saved.desconto ?? targetProposalForStudio.desconto })
        }
        toast.success('Apresentação salva com sucesso!', { id: toastId })
      } catch (err: unknown) {
        console.error(err)
        toast.error(getFriendlyError(err, 'Erro ao salvar apresentação'), { id: toastId })
      } finally {
        setStudioPlan(null)
      }
    },
    [targetProposalForStudio, onDealPatched, setProposals, setStudioPlan],
  )

  return { targetProposalForStudio, saveStudioPresentation }
}
